/**
 * React hook for debounced auto-save functionality
 */

import { useCallback, useRef, useEffect } from 'react'
import { ShowNotesBlock } from '../types'
import { useUpdateBlock } from './use-update-block'

interface UseAutoSaveOptions {
  delay?: number // Debounce delay in milliseconds
  enabled?: boolean // Whether auto-save is enabled
  basePath: string // API base path (e.g., "v2/create/show-notes" or "codex/chapters/show-notes")
}

/**
 * Hook for debounced auto-save of show notes blocks
 */
export function useAutoSave(options: UseAutoSaveOptions) {
  let { delay = 2000, enabled = true, basePath } = options
  let updateBlockMutation = useUpdateBlock(basePath)
  let timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  let autoSave = useCallback(
    (block: ShowNotesBlock) => {
      if (!enabled) return

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set new timeout for debounced save
      // eslint-disable-next-line fp/no-mutation
      timeoutRef.current = setTimeout(() => {
        updateBlockMutation.mutate(block)
      }, delay)
    },
    [updateBlockMutation, delay, enabled],
  )

  let cancelAutoSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      // eslint-disable-next-line fp/no-mutation
      timeoutRef.current = null
    }
  }, [])

  return {
    autoSave,
    cancelAutoSave,
    isSaving: updateBlockMutation.isPending,
    saveError: updateBlockMutation.error,
    isError: updateBlockMutation.isError,
  }
}
